import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Theme } from '../lib/themes';
import { useNavigation } from '../contexts/NavigationContext';
import DocumentUpload from './DocumentUpload';
import UserFactsPanel from './UserFactsPanel';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useMinLoading } from '../hooks/useMinLoading';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  sources?: Array<{ id: string; similarity: number; content: string; tier?: number }>;
  intent?: 'medical' | 'general' | 'research' | 'conversation';
  confidence?: {
    overall: number;
    reasoning: string;
  };
  source_quality?: {
    tier1: number;
    tier2: number;
    tier3: number;
    tier4Plus: number;
  };
  created_at?: string;
  processing_time_ms?: number;
  model_version?: string;
}

interface ChatInterfaceProps {
  theme: Theme;
  onMenuClick: () => void;
  onCloseSidebar: () => void;
  conversationId?: string;
  onConversationChange: (conversationId: string, title: string) => void;
  onNewConversation: () => void;
  sidebarCollapsed?: boolean;
}

export default function ChatInterface({ 
  theme, 
  onMenuClick,
  onCloseSidebar,
  conversationId: externalConversationId,
  onConversationChange,
  onNewConversation
}: ChatInterfaceProps) {
  const navigate = useNavigate();
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, withLoadingHistory] = useMinLoading(500);
  const [showUpload, setShowUpload] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [expandedSources, setExpandedSources] = useState<Set<number>>(new Set());
  const [conversationId, setConversationId] = useState<string | undefined>(externalConversationId);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { isMobile, setView } = useNavigation();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const toggleSources = (messageIndex: number) => {
    setExpandedSources((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(messageIndex)) {
        newSet.delete(messageIndex);
      } else {
        newSet.add(messageIndex);
      }
      return newSet;
    });
  };

  const formatTimestamp = (timestamp?: string) => {
    if (!timestamp) return '';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffSecs < 10) return 'Just now';
    if (diffSecs < 60) return `${diffSecs}s ago`;
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const formatProcessingTime = (ms?: number) => {
    if (!ms) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load conversation when external conversationId changes
  useEffect(() => {
    if (externalConversationId && externalConversationId !== conversationId) {
      loadConversation(externalConversationId);
    } else if (!externalConversationId && conversationId) {
      // Handle new chat - clear messages when external ID becomes undefined
      setMessages([]);
      setConversationId(undefined);
    }
  }, [externalConversationId]);

  const loadConversation = (convId: string) => withLoadingHistory(async () => {
    try {
      const response = await fetch(`${import.meta.env.VITE_API_URL}/conversations/${convId}`);
      const data = await response.json();
      
      if (data.success && data.conversation) {
        setConversationId(convId);
        const loadedMessages: Message[] = data.conversation.messages.map((msg: any) => ({
          role: msg.role,
          content: msg.content,
          sources: msg.sources || [],
          intent: msg.intent,
          confidence: msg.confidence_score ? {
            overall: msg.confidence_score,
            reasoning: msg.confidence_reasoning || ''
          } : undefined,
          created_at: msg.created_at,
          processing_time_ms: msg.processing_time_ms,
          model_version: msg.model_version,
        }));
        setMessages(loadedMessages);
      }
    } catch (error) {
      console.error('Failed to load conversation:', error);
    }
  });

  const handleNewChat = () => {
    setMessages([]);
    setConversationId(undefined);
    setInput('');
    setShowProfile(false);
    onNewConversation();
    onCloseSidebar();
    // Focus input after state updates
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const handleLogoClick = () => {
    // Navigate to main app view and reset state
    setShowProfile(false);
    handleNewChat();
    navigate('/app');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage: Message = { 
      role: 'user', 
      content: input,
      created_at: new Date().toISOString()
    };
    setMessages((prev) => [...prev, userMessage]);
    
    const userQuery = input;
    setInput('');
    setIsLoading(true);

    // Create new conversation if this is the first message
    let currentConvId = conversationId;
    if (!currentConvId && messages.length === 0) {
      try {
        const convResponse = await fetch(`${import.meta.env.VITE_API_URL}/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            title: userQuery.substring(0, 50) + (userQuery.length > 50 ? '...' : '')
          }),
        });
        const convData = await convResponse.json();
        if (convData.success && convData.conversation?.id) {
          currentConvId = convData.conversation.id;
          setConversationId(convData.conversation.id);
          onConversationChange(convData.conversation.id, convData.conversation.title);
        }
      } catch (error) {
        console.error('Failed to create conversation:', error);
      }
    }

    try {
      const response = await fetch(`${import.meta.env.VITE_CHAT_URL || import.meta.env.VITE_API_URL}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          query: userQuery,
          conversation_id: currentConvId,
          include_memory: true,
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        const assistantMessage: Message = {
          role: 'assistant',
          content: data.answer,
          sources: data.sources,
          intent: data.intent,
          confidence: data.confidence,
          source_quality: data.source_quality,
          created_at: new Date().toISOString(),
          processing_time_ms: data.processing_time_ms,
          model_version: data.model_version,
        };
        setMessages((prev) => [...prev, assistantMessage]);

        // Trigger memory processing in background (don't await)
        if (currentConvId) {
          fetch(`${import.meta.env.VITE_API_URL}/memory/process/${currentConvId}`, {
            method: 'POST',
          }).catch(err => console.error('Memory processing failed:', err));
        }
      } else {
        throw new Error(data.error || 'Failed to get response');
      }
    } catch (error) {
      const errorMessage: Message = {
        role: 'assistant',
        content: `Sorry, I encountered an error: ${error}`,
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const quickActions = [
    { icon: '📄', label: 'Upload document', action: () => setShowUpload(true) },
    { icon: '🔍', label: 'Search records', action: () => setInput('Search my medical records for ') },
    { icon: '📊', label: 'View summary', action: () => setInput('Give me a summary of my medical history') },
    { icon: '💊', label: 'Medications', action: () => setInput('What medications am I currently taking?') },
  ];

  // Add new chat button to header
  const showNewChatButton = messages.length > 0;

  return (
    <div className="flex flex-col" style={{ height: '100dvh', backgroundColor: theme.colors.background }}>
      {/* Header */}
      {isMobile ? (
        /* Mobile header — compact with logo, title, and action buttons */
        <header
          className="flex-shrink-0 border-b px-3 py-2"
          style={{
            borderColor: theme.colors.border,
            backgroundColor: theme.colors.background,
            boxShadow: '0 1px 0 rgba(255,255,255,0.05)'
          }}
        >
          <div className="flex items-center justify-between w-full">
            <button
              onClick={handleLogoClick}
              className="hover:opacity-80 transition-opacity"
              title="Go to main view"
            >
              <h1 className="text-base font-semibold" style={{ color: theme.colors.text }}>
                Project Lazarus
              </h1>
            </button>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="p-2 rounded-lg transition-all hover:bg-white/10"
                style={{ color: showProfile ? theme.colors.primary : theme.colors.textSecondary }}
                title="View your medical profile"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
              {!showProfile && showNewChatButton && (
                <button
                  onClick={handleNewChat}
                  className="px-2 py-1 rounded-lg text-xs font-medium transition-all hover:opacity-90"
                  style={{ backgroundColor: theme.colors.primary + '20', color: theme.colors.primary }}
                >
                  New Chat
                </button>
              )}
              <button
                onClick={onMenuClick}
                className="p-2 rounded-lg hover:bg-white/10 transition-all"
                style={{ color: theme.colors.text }}
                title="Toggle sidebar"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </header>
      ) : (
        /* Desktop header */
        <header
          className="flex-shrink-0 border-b px-4 py-3"
          style={{ 
            borderColor: theme.colors.border, 
            backgroundColor: theme.colors.background,
            boxShadow: '0 1px 0 rgba(255,255,255,0.05)'
          }}
        >
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <button
                onClick={onMenuClick}
                className="p-2 rounded-lg hover:bg-white/10 transition-all"
                style={{ color: theme.colors.text }}
                title="Toggle sidebar"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
              <button
                onClick={handleLogoClick}
                className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                title="Go to main view"
              >
                <img 
                  src="/logo.svg" 
                  alt="Project Lazarus" 
                  className="w-8 h-8"
                />
                <h1 className="text-xl font-semibold flex items-center gap-2" style={{ color: theme.colors.text }}>
                  <span>Project Lazarus</span>
                  {showProfile && (
                    <span style={{ color: theme.colors.textSecondary, opacity: 0.7 }}>
                      - Your Medical Profile
                    </span>
                  )}
                </h1>
              </button>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setView('graph')}
                className="p-2 rounded-lg transition-all hover:bg-white/10"
                style={{ color: theme.colors.textSecondary }}
                title="View Knowledge Graph"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </button>
              <button
                onClick={() => setShowProfile(!showProfile)}
                className="p-2 rounded-lg transition-all hover:bg-white/10"
                style={{ color: showProfile ? theme.colors.primary : theme.colors.textSecondary }}
                title="View your medical profile"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              </button>
              {!showProfile && showNewChatButton && (
                <button
                  onClick={handleNewChat}
                  className="px-3 py-1.5 rounded-lg text-sm font-medium transition-all hover:opacity-90"
                  style={{ backgroundColor: theme.colors.primary + '20', color: theme.colors.primary }}
                >
                  New Chat
                </button>
              )}
              <span className="text-xs px-2 py-1 rounded-full" style={{ backgroundColor: theme.colors.primary + '20', color: theme.colors.primary }}>
                HIPAA Compliant
              </span>
            </div>
          </div>
        </header>
      )}

      {showProfile ? (
        <UserFactsPanel theme={theme} onClose={() => setShowProfile(false)} />
      ) : (
        <>
        {/* Messages Area - scrollable */}
        <div className="flex-1 overflow-y-auto">
        {isLoadingHistory ? (
          <div className="flex items-center justify-center h-full" style={{ minHeight: 'calc(100dvh - 130px)' }}>
            <div className="flex flex-col items-center gap-4">
              <div className="relative w-12 h-12">
                <div className="absolute inset-0 rounded-full border-4 opacity-20" style={{ borderColor: theme.colors.primary }} />
                <div className="absolute inset-0 rounded-full border-4 border-t-transparent animate-spin" style={{ borderColor: theme.colors.primary }} />
              </div>
              <p className="text-sm font-medium" style={{ color: theme.colors.textSecondary }}>Loading conversation...</p>
            </div>
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-4 py-16" style={{ minHeight: 'calc(100dvh - 130px)' }}>
            <div className="max-w-2xl w-full space-y-6 animate-slide-in">
              {/* Welcome Message with Logo */}
              <div className="text-center space-y-6 mt-16">
                <img 
                  src="/logo.svg" 
                  alt="Project Lazarus" 
                  className="mx-auto"
                  style={{ width: '128px', height: '128px' }}
                />
                <div className="text-sm" style={{ color: theme.colors.textSecondary }}>
                  Hi there
                </div>
              </div>

              {/* Quick Actions */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={action.action}
                    className="flex items-center gap-3 p-4 rounded-2xl text-left transition-all hover:scale-[1.02]"
                    style={{
                      backgroundColor: theme.colors.surface,
                      border: `1px solid ${theme.colors.border}`,
                      color: theme.colors.text,
                    }}
                  >
                    <span className="text-2xl">{action.icon}</span>
                    <span className="text-sm font-medium">{action.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="max-w-3xl mx-auto px-4 py-8 space-y-6">
            {messages.map((message, index) => (
              <div
                key={index}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'} animate-slide-in`}
              >
                <div
                  className={`max-w-[85%] rounded-3xl px-6 py-4 ${
                    message.role === 'user'
                      ? 'rounded-br-md'
                      : 'rounded-bl-md'
                  }`}
                  style={{
                    backgroundColor: message.role === 'user' ? theme.colors.primary : theme.colors.surface,
                    color: message.role === 'user' ? '#ffffff' : theme.colors.text,
                    border: message.role === 'assistant' ? `1px solid ${theme.colors.border}` : 'none',
                  }}
                >
                  {message.role === 'assistant' ? (
                    <div className="markdown-content">
                      {/* Intent and Confidence Badges */}
                      {(message.intent || message.confidence) && (
                        <div className="flex items-center gap-2 mb-3 pb-3 border-b" style={{ borderColor: theme.colors.border }}>
                          {message.intent && (
                            <span className="text-xs px-2 py-1 rounded-full" style={{ 
                              backgroundColor: theme.colors.background,
                              color: theme.colors.textSecondary 
                            }}>
                              {message.intent === 'medical' && '🏥 Medical'}
                              {message.intent === 'research' && '🔍 Research'}
                              {message.intent === 'general' && '💬 General'}
                              {message.intent === 'conversation' && '💭 Chat'}
                            </span>
                          )}
                          {message.confidence && (
                            <span 
                              className="text-xs px-2 py-1 rounded-full" 
                              style={{ 
                                backgroundColor: message.confidence.overall >= 0.8 
                                  ? theme.colors.primary + '20' 
                                  : message.confidence.overall >= 0.5 
                                    ? theme.colors.background 
                                    : '#ff990020',
                                color: message.confidence.overall >= 0.8 
                                  ? theme.colors.primary 
                                  : message.confidence.overall >= 0.5 
                                    ? theme.colors.textSecondary 
                                    : '#ff9900'
                              }}
                              title={message.confidence.reasoning}
                            >
                              {message.confidence.overall >= 0.8 ? '✓ High Confidence' : 
                               message.confidence.overall >= 0.5 ? 'ℹ️ Moderate Confidence' : 
                               '⚠️ Low Confidence'}
                            </span>
                          )}
                          {message.source_quality && (
                            <span className="text-xs opacity-60">
                              {message.source_quality.tier1 > 0 && `${message.source_quality.tier1} medical records`}
                              {message.source_quality.tier2 > 0 && ` • ${message.source_quality.tier2} authoritative`}
                              {message.source_quality.tier3 > 0 && ` • ${message.source_quality.tier3} professional`}
                            </span>
                          )}
                        </div>
                      )}
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ node, ...props }) => <h1 {...props} />,
                          h2: ({ node, ...props }) => <h2 {...props} />,
                          h3: ({ node, ...props }) => <h3 {...props} />,
                          p: ({ node, ...props }) => <p {...props} />,
                          ul: ({ node, ...props }) => <ul {...props} />,
                          ol: ({ node, ...props }) => <ol {...props} />,
                          li: ({ node, ...props }) => <li {...props} />,
                          strong: ({ node, ...props }) => <strong style={{ color: theme.colors.primary }} {...props} />,
                          code: ({ node, inline, ...props }: any) => 
                            inline ? (
                              <code className="px-1.5 py-0.5 rounded text-sm" style={{ backgroundColor: theme.colors.background }} {...props} />
                            ) : (
                              <code className="block p-3 rounded-lg text-sm overflow-x-auto" style={{ backgroundColor: theme.colors.background }} {...props} />
                            ),
                          blockquote: ({ node, ...props }) => (
                            <blockquote className="border-l-4 pl-4" style={{ borderColor: theme.colors.primary }} {...props} />
                          ),
                        }}
                      >
                        {message.content}
                      </ReactMarkdown>
                    </div>
                  ) : (
                    <p className="text-base leading-relaxed whitespace-pre-wrap">{message.content}</p>
                  )}
                  
                  {/* Message Footer - Timestamp and Metadata */}
                  <div className="flex items-center gap-2 mt-3 pt-2 border-t" style={{ borderColor: theme.colors.border + '40' }}>
                    {message.created_at && (
                      <span className="text-xs opacity-60">
                        {formatTimestamp(message.created_at)}
                      </span>
                    )}
                    {message.role === 'assistant' && message.processing_time_ms && (
                      <>
                        <span className="text-xs opacity-40">•</span>
                        <span className="text-xs opacity-60" title="Processing time">
                          ⚡ {formatProcessingTime(message.processing_time_ms)}
                        </span>
                      </>
                    )}
                    {message.role === 'assistant' && message.model_version && (
                      <>
                        <span className="text-xs opacity-40">•</span>
                        <span className="text-xs opacity-60" title="AI Model">
                          🤖 {message.model_version.includes('claude') ? 'Claude' : message.model_version}
                        </span>
                      </>
                    )}
                  </div>
                  
                  {message.sources && message.sources.length > 0 && (
                    <div className="mt-4 pt-4 border-t" style={{ borderColor: theme.colors.border }}>
                      <button
                        onClick={() => toggleSources(index)}
                        className="flex items-center justify-between w-full text-left hover:opacity-80 transition-opacity"
                      >
                        <p className="text-xs font-semibold uppercase tracking-wide opacity-70">
                          Sources ({message.sources.length})
                        </p>
                        <svg
                          className={`w-4 h-4 transition-transform ${expandedSources.has(index) ? 'rotate-180' : ''}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                      {expandedSources.has(index) && (
                        <div className="mt-2 space-y-2 animate-slide-in">
                          {message.sources.map((source, idx) => (
                            <div key={idx} className="text-xs opacity-75 p-3 rounded-xl" style={{ backgroundColor: theme.colors.background }}>
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-medium">
                                  {(source.similarity * 100).toFixed(0)}% match
                                </span>
                              </div>
                              <p className="line-clamp-2">{source.content}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start animate-slide-in">
                <div className="px-6 py-4 rounded-3xl rounded-bl-md" style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}` }}>
                  <div className="flex items-center gap-2">
                    <div className="flex space-x-1">
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: theme.colors.primary }}></div>
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: theme.colors.primary, animationDelay: '0.1s' }}></div>
                      <div className="w-2 h-2 rounded-full animate-bounce" style={{ backgroundColor: theme.colors.primary, animationDelay: '0.2s' }}></div>
                    </div>
                    <span className="text-sm" style={{ color: theme.colors.textSecondary }}>Thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area - pinned to bottom, sits above mobile nav */}
      <div
        className="flex-shrink-0 p-4 md:p-6 border-t sticky bottom-0 pb-[calc(1rem+env(safe-area-inset-bottom))] lg:pb-6"
        style={{
          backgroundColor: theme.colors.background,
          borderColor: theme.colors.border,
          marginBottom: isMobile ? '64px' : 0,
        }}
      >
        <div className="max-w-3xl mx-auto">
          <form onSubmit={handleSubmit} className="relative">
            <div className="flex items-end gap-2 p-2 rounded-3xl" style={{ backgroundColor: theme.colors.surface, border: `1px solid ${theme.colors.border}` }}>
              <button
                type="button"
                onClick={() => setShowUpload(true)}
                className="p-3 rounded-xl hover:bg-white/10 transition-all flex-shrink-0"
                style={{ color: theme.colors.textSecondary }}
                title="Upload document"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              </button>
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about your health..."
                className="flex-1 bg-transparent px-2 py-3 focus:outline-none text-base resize-none"
                style={{ color: theme.colors.text, minHeight: '24px', maxHeight: '200px' }}
                disabled={isLoading}
                rows={1}
                onInput={(e) => {
                  const target = e.target as HTMLTextAreaElement;
                  target.style.height = '24px';
                  target.style.height = Math.min(target.scrollHeight, 200) + 'px';
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
              <button
                type="submit"
                disabled={isLoading || !input.trim()}
                className="p-3 rounded-xl transition-all flex-shrink-0 disabled:opacity-50"
                style={{ backgroundColor: theme.colors.primary, color: '#ffffff' }}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                </svg>
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Upload Modal */}
      {showUpload && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <DocumentUpload theme={theme} onClose={() => setShowUpload(false)} />
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}
